import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Pagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import Notification from 'components/notification';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';

import colorsStore from 'store/colorsStore';
import DraggableList from 'components/draggableList';
import { IColor, IPaginationResponse, EnumPath } from 'types/types';

const Colors: FC = () => {
  const [params, setParams] = useState({
    page: 1,
    perpage: 20,
    paginate: true
  });

  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState('');
  const [notificationStatus, setNotificationStatus] = useState<
    'error' | 'success'
  >('success');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [colorToDelete, setColorToDelete] = useState<number | null>(null);

  const navigate = useNavigate();

  const notifyUser = (message: string, status: 'error' | 'success') => {
    setNotificationMsg(message);
    setNotificationStatus(status);
    setIsNotificationOpen(true);
  };

  const isPaginated = <T,>(
    data: T[] | IPaginationResponse<T>
  ): data is IPaginationResponse<T> => {
    return (data as IPaginationResponse<T>)?.data !== undefined;
  };

  useEffect(() => {
    colorsStore.getColors(params);
    return () => {
      colorsStore.clearAll();
    };
  }, [params]);

  const handleDeleteClick = (id: number) => {
    setColorToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (colorToDelete === null) return;

    const res = await colorsStore.deleteColor(colorToDelete);
    notifyUser(res.msg, res.isOk ? 'success' : 'error');
    if (res.isOk) {
      colorsStore.getColors(params);
    }
    setDeleteDialogOpen(false);
    setColorToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setColorToDelete(null);
  };

  const handleCreateClick = () => {
    navigate('/complex-attributes/colors/create');
  };

  const isLoadingInitial =
    colorsStore.loading && !isPaginated(colorsStore.colors);

  // eslint-disable-next-line no-nested-ternary
  const items: IColor[] = isPaginated<IColor>(colorsStore.colors)
    ? colorsStore.colors.data
    : Array.isArray(colorsStore.colors)
    ? colorsStore.colors
    : [];

  return (
    <Box sx={{ p: 3 }}>
      <Notification
        open={isNotificationOpen}
        message={notificationMsg}
        onClose={() => {
          setIsNotificationOpen(false);
          setNotificationMsg('');
        }}
        severity={notificationStatus}
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}
      >
        <Typography variant="h4">Цвета</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateClick}
        >
          Добавить цвет
        </Button>
      </Box>

      <DraggableList
        list={items}
        pagesQuantity={
          isPaginated<IColor>(colorsStore.colors)
            ? colorsStore.colors.last_page
            : 1
        }
        isSortable
        notifyUser={(msg, status) =>
          notifyUser(msg, status === 'warning' ? 'success' : status)
        }
        onReload={() => colorsStore.getColors(params)}
        path={EnumPath.colors}
        renderItem={(item) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              width: '100%'
            }}
          >
            <Box
              className="drag-handle"
              sx={{
                cursor: 'grab',
                width: 24,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              ≡
            </Box>

            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                backgroundColor: item.hex_code,
                border: '1px solid #ddd'
              }}
            />

            <Button
              variant="text"
              onClick={() => navigate(`/complex-attributes/colors/${item.id}`)}
              sx={{
                color: 'inherit',
                textAlign: 'left',
                justifyContent: 'flex-start',
                textTransform: 'none',
                p: 0,
                minWidth: 0
              }}
            >
              {item.name}
            </Button>

            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
              <Tooltip title="Редактировать">
                <IconButton
                  size="small"
                  onClick={() =>
                    navigate(`/complex-attributes/colors/${item.id}`)
                  }
                >
                  <EditIcon fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Удалить">
                <IconButton
                  size="small"
                  onClick={() => handleDeleteClick(item.id)}
                >
                  <DeleteIcon fontSize="small" color="error" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
      />

      {/* Пагинация */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Pagination
          count={
            isPaginated<IColor>(colorsStore.colors)
              ? colorsStore.colors.last_page
              : 1
          }
          page={params.page}
          onChange={(_, pageNum) => {
            setParams((prev) => ({ ...prev, page: pageNum }));
          }}
          color="primary"
          size="large"
        />
      </Box>

      {/* Диалог подтверждения удаления */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Подтверждение удаления
        </DialogTitle>
        <DialogContent>
          <DialogContent id="delete-dialog-description">
            Вы уверены, что хотите удалить цвет? Это действие нельзя отменить.
          </DialogContent>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Отмена
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default observer(Colors);
